import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import './StayAtGlanceSection.css';
import { stayProperties } from './stayProperties';
import PropertyBookingCard from '../components/PropertyBookingCard';

gsap.registerPlugin(useGSAP, ScrollTrigger);

export default function StayAtGlanceSection({ property = stayProperties.georgia }) {
  const scope = useRef(null);

  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const q = gsap.utils.selector(scope);
      gsap.timeline({
        defaults: { duration: .8, ease: 'power3.out', clearProps: 'transform,opacity,visibility' },
        scrollTrigger: { trigger: scope.current, start: 'top 82%', once: true },
      })
        .from(q('.stay-glance__eyebrow'), { autoAlpha: 0, y: 8 })
        .from(q('.stay-glance__highlight'), { autoAlpha: 0, y: 15 }, .1)
        .from(q('.stay-glance__divider'), { scaleX: 0, transformOrigin: 'left center', duration: .65 }, .2)
        .from(q('.stay-glance__main-copy'), { autoAlpha: 0, y: 25 }, .3)
        .from(q('.stay-glance__intro'), { autoAlpha: 0, y: 18 }, .4)
        .from(q('.stay-glance__details'), { autoAlpha: 0, y: 22 }, .5)
        .from(q('.stay-glance__booking'), { autoAlpha: 0, x: 35, scale: .98 }, .3)
        .from(q('.stay-glance__booking h3, .stay-glance__field, .stay-glance__button, .stay-glance__note'), { autoAlpha: 0, y: 12, stagger: .08, duration: .65 }, .5);
    });
    return () => media.revert();
  }, { scope });

  return (
    <section ref={scope} className="stay-glance" id="plan-your-stay" aria-labelledby="stay-glance-title">
      <div className="stay-glance__inner">
        <div className="stay-glance__header">
          <p className="stay-glance__eyebrow">YOUR STAY AT A GLANCE</p>
          <h2 className="stay-glance__highlight" id="stay-glance-title">{property.overview}</h2>
        </div>
        <dl className="property-facts"><div><dt>Destination</dt><dd>{property.destination}</dd></div><div><dt>Bedrooms</dt><dd>{property.bedrooms || 'Ask your host'}</dd></div><div><dt>Bathrooms</dt><dd>{property.bathrooms || 'Ask your host'}</dd></div></dl><div className="stay-glance__divider" aria-hidden="true" />
        <div className="stay-glance__layout">
          <div className="stay-glance__content">
            <div className="stay-glance__main-copy">
              <h3>Settle in. Feel at home.</h3>
            </div>
            <p className="stay-glance__intro">{property.intro || 'Whether you’re here for work, visiting Valdosta State University or spending time with loved ones, make 309 Rosedale Place your starting point. With SGMC and VSU nearby, this Valdosta address puts the places that bring you to town at the heart of your plans.'}</p>
            <p className="stay-glance__intro stay-glance__intro--more">{property.more || 'Planning a short visit or a longer stay? Tell your host what brings you here, who’s travelling with you and the dates you have in mind. It’s a helpful first step towards finding out whether the home suits your trip.'}</p>
            <div className="stay-glance__details">
              <p className="stay-glance__eyebrow">THE DETAILS</p>
              <h3>A home for your kind of journey.</h3>
              <p>Get to know the location and discuss the details that matter to you before booking. Your host can help with questions about the home, arrival arrangements and your intended length of stay.</p>
              <div className="stay-glance__property-highlights">
                <h4>Property Highlights</h4>
                <ul>
                  <li><strong>Home Away From Home Comfort</strong> — Thoughtfully prepared spaces designed to make every guest feel relaxed, welcomed, and completely at ease from the moment they arrive.</li>
                  <li><strong>Time to recharge</strong> — Set your own pace, from a quiet morning to a day exploring the destination.</li>
                  <li><strong>Warm hospitality</strong> — A thoughtful welcome and a comfortable place to settle in.</li>
                  <li><strong>Amenities</strong> — Review the features of your chosen stay and ask your host about anything you need.</li>
                  <li><strong>A change of scene</strong> — Make room for rest and local experiences.</li>
                  <li><strong>Convenient Location</strong> — Easy access to local attractions, dining, and transportation, making it simple to explore and enjoy the area.</li>
                </ul>
              </div>
              <h4>Amenities</h4>{property.amenities.length ? <ul className="stay-glance__amenities">{property.amenities.map(amenity => <li key={amenity}>{amenity}</li>)}</ul> : <p>Contact your host for the amenities available at this stay.</p>}<div className="stay-glance__facts">
                {property.facts ? property.facts.map(([title, copy]) => <article key={title}><h4>{title}</h4><p>{copy}</p></article>) : <>
                <article><h4>A Valdosta starting point</h4><p>Find us at 309 Rosedale Place, Valdosta, GA 31602. Plan your journeys to SGMC, VSU and the other places on your itinerary before you arrive.</p></article>
                <article><h4>Arriving by car</h4><p>The home features a circular driveway. Ask your host about parking arrangements and vehicle space so you know what to expect on arrival.</p></article>
                <article><h4>Make the stay fit your plans</h4><p>Share your preferred dates, group size and any specific needs. Confirm guest capacity, sleeping arrangements and the amenities you need with your host.</p></article>
                <article><h4>A closer look is coming</h4><p>Home photography and the full amenities list will be added. The destination image above sets the scene; contact your host for more information about the home itself.</p></article>
                <article><h4>Thoughtful Touches</h4><p>Every detail—from décor to layout—is chosen with your comfort in mind, creating a space that feels personal and inviting.</p></article>
                <article><h4>Amenities</h4><ul className="stay-glance__amenities"><li>Circular driveway</li><li>Washer and dryer</li></ul></article>
                </>}
              </div>
              <a className="stay-glance__contact" href="/contact">Ask your host about this stay <span aria-hidden="true">↗</span></a>
            </div>
          </div>
          <PropertyBookingCard property={property} />
        </div>
      </div>
    </section>
  );
}
